import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "utils/caseDashboard";
import { SEVERITY_COLORS, SEVERITY_LEVELS } from "utils/caseLiveStats";

// Small pieces shared by the Live session view.

/** Date.now(), refreshed every `ms` while `enabled`, so "12s ago" keeps moving. */
export function useNow(ms = 1000, enabled = true) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
  return now;
}

/** "just now" under 5 s, else "12s ago" / "3m ago". */
export function useAgo() {
  const { t } = useTranslation();
  return (then, now) => {
    if (then == null) return t("caseDashboard.live.never");
    const s = (now - then) / 1000;
    if (s < 5) return t("caseDashboard.live.justNow");
    return t("caseDashboard.live.heardAgo", { time: formatDuration(s) });
  };
}

export const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

export const Avatar = ({ name, color, size = 32 }) => (
  <span
    className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
    style={{
      width: size,
      height: size,
      fontSize: Math.round(size * 0.38),
      background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
    }}
  >
    {initials(name)}
  </span>
);

const STATUS_STYLE = {
  live: { bg: "#DCFCE7", fg: "#15803D", dot: "#16A34A", pulse: true },
  quiet: { bg: "#FEF3E7", fg: "#B4531A", dot: "#E07A3A" },
  silent: { bg: "#FEE2E2", fg: "#B91C1C", dot: "#DC2626" },
  never: { bg: "#F3F4F6", fg: "#6B7280", dot: "#9CA3AF" },
  ended: { bg: "#F3F4F6", fg: "#6B7280", dot: "#9CA3AF" },
};

export const StatusPill = ({ status }) => {
  const { t } = useTranslation();
  const s = STATUS_STYLE[status] || STATUS_STYLE.never;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      <span className="relative inline-flex h-1.5 w-1.5">
        {s.pulse && (
          <span
            className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
            style={{ backgroundColor: s.dot }}
          />
        )}
        <span
          className="relative inline-flex h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: s.dot }}
        />
      </span>
      {t(`caseDashboard.live.status.${status}`)}
    </span>
  );
};

/** Thin battery gauge; red under 20 %. */
export const BatteryBar = ({ pct }) => {
  if (pct == null) return <span className="text-[11px] text-[#9CA3AF]">—</span>;
  const color = pct < 20 ? "#DC2626" : pct < 40 ? "#E07A3A" : "#10B981";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="relative h-2 w-7 rounded-[3px] border border-[#D1D5DB] p-[1px]">
        <span
          className="block h-full rounded-[1px]"
          style={{ width: `${Math.max(4, pct)}%`, backgroundColor: color }}
        />
        <span className="absolute -right-[3px] top-[2px] h-[3px] w-[2px] rounded-r bg-[#D1D5DB]" />
      </span>
      <span
        className={`text-[11px] font-medium ${
          pct < 20 ? "text-[#DC2626]" : "text-[#6B7280]"
        }`}
      >
        {pct}%
      </span>
    </span>
  );
};

/** A small trend line for a measurement, scaled to its own range. */
export const TrendLine = ({
  values,
  color = "#E07A3A",
  height = 28,
  className = "",
}) => {
  if (values.length < 2) return null;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const pts = values.map((v, i) => [
    (i / (values.length - 1)) * 100,
    height - 3 - ((v - lo) / span) * (height - 6),
  ]);
  const line = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const id = `tl-${color.replace("#", "")}`;
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className={`w-full ${className}`}
      style={{ height }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${line} 100,${height}`}
        fill={`url(#${id})`}
      />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

/** Bars for a series of counts. */
export const Sparkline = ({
  values,
  color = "#E07A3A",
  height = 28,
  className = "",
}) => {
  const max = Math.max(1, ...values);
  const w = 100 / values.length;
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className={`w-full ${className}`}
      style={{ height }}
      aria-hidden="true"
    >
      {values.map((v, i) => {
        const h = v ? Math.max(2, (v / max) * (height - 2)) : 1;
        return (
          <rect
            key={i}
            x={i * w + w * 0.15}
            y={height - h}
            width={w * 0.7}
            height={h}
            rx={0.8}
            fill={color}
            opacity={v ? 0.35 + 0.65 * (i / values.length) : 0.15}
          />
        );
      })}
    </svg>
  );
};

/** One stacked bar of light / moderate / hard / severe counts. */
export const SeverityBar = ({ counts, height = 6, className = "" }) => {
  const total = counts.reduce((a, b) => a + b, 0);
  return (
    <div
      className={`flex w-full overflow-hidden rounded-full bg-[#F3F4F6] ${className}`}
      style={{ height }}
    >
      {total > 0 &&
        counts.map((c, i) =>
          c ? (
            <span
              key={i}
              style={{
                width: `${(c / total) * 100}%`,
                backgroundColor: SEVERITY_COLORS[i],
              }}
            />
          ) : null
        )}
    </div>
  );
};

/** Legend for the severity colours; `counts` hides levels that never occur. */
export const SeverityLegend = ({ counts, className = "" }) => {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {SEVERITY_LEVELS.map((level, i) =>
        counts && !counts[i] ? null : (
          <span
            key={level}
            className="flex items-center gap-1.5 text-[11px] text-[#6B7280]"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: SEVERITY_COLORS[i] }}
            />
            {t(`caseDashboard.live.severity.${level}`)}
          </span>
        )
      )}
    </div>
  );
};

/** A row of toggle buttons. */
export const Segmented = ({ options, value, onChange, size = "sm" }) => (
  <div className="inline-flex rounded-lg bg-[#F3F4F6] p-0.5">
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`rounded-md font-medium transition duration-150 ${
          size === "sm" ? "px-2.5 py-1 text-[12px]" : "px-3 py-1.5 text-[13px]"
        } ${
          value === o.value
            ? "bg-white text-[#111827] shadow-sm"
            : "text-[#6B7280] hover:text-[#374151]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const gText = (t, g) =>
  t("caseDashboard.live.gValue", { value: Number(g).toFixed(1) });
